import { t } from "@redotech/redo-i18n";
import {
  normalizeSettingsSearchPhrase,
  settingsSearchTextMatches,
} from "@redotech/redo-web/settings-search/settings-search";
import { assertNever } from "@redotech/util/type";

export const OMS_SETTINGS_SECTION_QUERY = "section";

export const omsSettingsTabs = [
  "order-preparation",
  "display",
  "locations",
  "international",
  "merging",
  "splitting",
  "fulfillment-promise",
  "printing",
  "presets-shortcuts",
  "access-devices",
] as const;

export type OmsSettingsTab = (typeof omsSettingsTabs)[number];

export function getOmsSettingsTabLabel(tab: OmsSettingsTab): string {
  switch (tab) {
    case "order-preparation":
      return t("Order preparation");
    case "display":
      return t("Display");
    case "locations":
      return t("Locations");
    case "international":
      return t("International");
    case "merging":
      return t("Merging");
    case "splitting":
      return t("Splitting");
    case "fulfillment-promise":
      return t("Fulfillment promise");
    case "printing":
      return t("Printing");
    case "presets-shortcuts":
      return t("Presets & Shortcuts");
    case "access-devices":
      return t("Access & devices");
    default:
      return assertNever(tab);
  }
}

interface OmsSettingsTabSearchItem {
  label: string;
  searchTerms: readonly string[];
}

const omsSettingsTabSearchItems: Record<
  OmsSettingsTab,
  OmsSettingsTabSearchItem
> = {
  "order-preparation": {
    label: "Order preparation",
    searchTerms: [
      "order intake",
      "packages",
      "parcels",
      "package dimensions",
      "package weight",
      "addresses",
      "address validation",
      "address exceptions & outreach",
      "order eligibility",
      "pickup & carrier handoff",
      "USPS manifest",
      "protected fulfillment actions",
      "parcel verification photos",
      "fulfillment defaults",
      "batch suggestions",
      "bulk shipment suggestions",
      "bulk shipment matching",
      "remember shipment choices",
      "remember shipment settings",
      "automated learning",
    ],
  },
  display: {
    label: "Display",
    searchTerms: [
      "display & units",
      "weight unit",
      "custom field mappings",
      "custom fulfillment data",
      "custom attributes",
      "live unfulfilled order counts",
    ],
  },
  locations: {
    label: "Locations",
    searchTerms: [
      "fulfillment locations",
      "ship from locations",
      "return addresses",
      "shipping origins",
      "warehouses",
    ],
  },
  international: {
    label: "International",
    searchTerms: [
      "international shipping",
      "international shipments",
      "default customs information",
      "customs declarations",
      "tax identifiers",
      "AES exemption code",
    ],
  },
  merging: {
    label: "Merging",
    searchTerms: [
      "fulfillment merging",
      "shipment merging",
      "merge & split",
      "merge suggestions",
      "auto-merge criteria",
      "merged shipment outcomes",
      "merge exceptions",
    ],
  },
  splitting: {
    label: "Splitting",
    searchTerms: [
      "fulfillment splitting",
      "shipment splitting",
      "merge & split",
      "auto-split orders",
      "auto-split conditions",
      "split conditions",
      "when to split",
      "how to split",
      "after splitting",
      "split exceptions",
      "product SKU mappings",
    ],
  },
  "fulfillment-promise": {
    label: "Fulfillment promise",
    searchTerms: [
      "custom fulfillment cutoff schedule",
      "fulfillment cutoff",
      "fulfillment schedule",
      "use a custom schedule",
      "working days & cutoffs",
      "fulfillment promise days",
      "timezone",
    ],
  },
  printing: {
    label: "Printing",
    searchTerms: [
      "print profiles",
      "documents",
      "pick list",
      "printers & workstations",
      "fulfillment actions",
      "default action button",
      "one click print",
    ],
  },
  "presets-shortcuts": {
    label: "Presets & Shortcuts",
    searchTerms: [
      "shipping presets",
      "fulfillment presets",
      "keyboard shortcuts",
      "hotkeys",
      "custom imports",
      "custom import templates",
      "custom import schemas",
      "CSV column mappings",
      "import templates",
    ],
  },
  "access-devices": {
    label: "Access & devices",
    searchTerms: [
      "security",
      "PIN sign-in",
      "PIN verification",
      "device authentication",
      "authorized devices",
      "enable device",
      "revoke device",
    ],
  },
};

/** A tab's search terms, so sidebar search can link straight to it. */
export function getOmsSettingsTabSearchTerms(
  tab: OmsSettingsTab,
): readonly string[] {
  return omsSettingsTabSearchItems[tab].searchTerms;
}

export function getVisibleOmsSettingsTabs({
  canManageFulfillmentSettings,
  canManageShippingSettings,
  canManageTeamUsers,
  omsEnabled,
  restrictedShippingTeam,
}: {
  canManageFulfillmentSettings: boolean;
  canManageShippingSettings: boolean;
  canManageTeamUsers: boolean;
  omsEnabled: boolean;
  restrictedShippingTeam: boolean;
}): OmsSettingsTab[] {
  return omsSettingsTabs.filter((tab) => {
    if (tab === "access-devices") {
      return omsEnabled && canManageFulfillmentSettings && canManageTeamUsers;
    }
    if (
      tab === "order-preparation" ||
      tab === "display" ||
      tab === "locations" ||
      tab === "international" ||
      tab === "merging" ||
      tab === "splitting" ||
      tab === "printing" ||
      tab === "presets-shortcuts"
    ) {
      return omsEnabled && canManageFulfillmentSettings;
    }
    if (tab === "fulfillment-promise") {
      return canManageShippingSettings && !restrictedShippingTeam;
    }
    return false;
  });
}

export function getOmsSettingsTabForSearch({
  search,
  visibleTabs,
}: {
  search: string;
  visibleTabs: readonly OmsSettingsTab[];
}): OmsSettingsTab | undefined {
  if (!search.trim()) {
    return undefined;
  }

  const normalizedSearch = normalizeSettingsSearchPhrase(search);
  return visibleTabs
    .map((tab, index) => {
      const item = omsSettingsTabSearchItems[tab];
      const phrases = [item.label, ...item.searchTerms];
      return {
        exactMatch: phrases.some(
          (phrase) =>
            normalizeSettingsSearchPhrase(phrase) === normalizedSearch,
        ),
        index,
        matches: phrases.some((phrase) =>
          settingsSearchTextMatches({ search, text: phrase }),
        ),
        tab,
      };
    })
    .filter(({ matches }) => matches)
    .sort(
      (a, b) =>
        Number(b.exactMatch) - Number(a.exactMatch) || a.index - b.index,
    )[0]?.tab;
}

export function getOmsSettingsTabForLocationLink({
  group,
  setting,
  hash,
  visibleTabs,
}: {
  group: string | null;
  setting: string | null;
  hash: string;
  visibleTabs: readonly OmsSettingsTab[];
}): OmsSettingsTab | undefined {
  if (
    visibleTabs.includes("order-preparation") &&
    (group === "fulfillment.packages" || hash === "#packages")
  ) {
    return "order-preparation";
  }
  if (
    visibleTabs.includes("presets-shortcuts") &&
    group === "fulfillment.custom_imports"
  ) {
    return "presets-shortcuts";
  }
  if (
    visibleTabs.includes("access-devices") &&
    (group === "fulfillment.security.pin_sign_in" ||
      group === "fulfillment.security.authorized_devices" ||
      setting === "security/methods/deviceAuthAndPin")
  ) {
    return "access-devices";
  }
  if (
    visibleTabs.includes("display") &&
    (group === "fulfillment.display" ||
      group === "fulfillment.custom_field_mappings" ||
      [
        "outboundLabels/weightFormat",
        "outboundLabels/degroupItemsByCustomAttributes",
        "outboundLabels/hideUnderscoreCustomAttributes",
        "outboundLabels/liveViewCountsEnabled",
        "outboundLabels/customMetadata",
        "outboundLabels/customAttributes",
        "outboundLabels/productCustomMetadata",
        "outboundLabels/variantCustomMetadata",
      ].includes(setting ?? ""))
  ) {
    return "display";
  }
  if (
    visibleTabs.includes("locations") &&
    (group === "fulfillment.ship_from_locations" ||
      setting === "outboundLabels/defaultShipFromLocationExternalId" ||
      hash === "#locations")
  ) {
    return "locations";
  }
  return undefined;
}
